import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AddTenantForm from "../Forms/AddTenantForm";
import AddResidentForm from "./AddResidentForm";
import AllResidents from "./AllResidents";
import LongTermLeases from "../TenantManagement/LongTermLeases";
import AnimatedModal from "../Components/AnimatedModal";
import StackingPlan from "../PropertyManagement/StackingPlan";
import { IoMdAdd } from "react-icons/io";
import { FaFileDownload, FaFileUpload } from "react-icons/fa";
import { createNewDocument } from "../../../utils/firebase/Firebase.utils";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { FiX, FiFileText } from "react-icons/fi";

function TenantManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const link = searchParams.get("link");
  const [activeLink, setActiveLink] = React.useState(link || "Owners");
  const [addMode, setAddMode] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const fileInputRef = useRef(null);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const currentUser = useSelector((state) => state.userObject);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [recordCount, setRecordCount] = useState(0);
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);

  useEffect(() => {
    setSearchParams({ link: activeLink });
  }, [activeLink]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const downloadCSVTemplate = () => {
    const templateHeaders = [
      "Owner 1",
      "Owner 2",
      "Nationality",
      "Address",
      "Email",
      "Phone Number",
      "Tower",
      "Floor",
      "Unit Number",
      "Unit Type",
      "Unit Size (sqm)",
      "Number of Occupants",
    ];

    const csvContent = [
      templateHeaders.join(","),
      templateHeaders.map(() => "").join(","),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "resident_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const csvContent = event.target.result;
        const rows = csvContent.split("\n").filter((row) => row.trim());
        const headers = rows[0].split(",").map((header) => header.trim());
        setCsvHeaders(headers);

        // Skip header row and process all non-empty rows
        const validRows = rows.slice(1).filter((row) => {
          const values = row.split(",").map((value) => value.trim());
          return values.some((value) => value !== "");
        });

        const recordCount = validRows.length;

        // Create preview of all rows (not just 5)
        const preview = validRows.map((row) => {
          const values = row.split(",").map((value) => value.trim());
          return headers.reduce((obj, header, index) => {
            obj[header] = values[index] || "";
            return obj;
          }, {});
        });

        setCsvPreview(preview);

        if (recordCount > 0) {
          setCsvData(csvContent);
          setRecordCount(recordCount);
          setShowUploadModal(true);
        } else {
          toast.error("No valid records found in the CSV file");
        }
      } catch (error) {
        console.error("Error reading CSV file:", error);
        toast.error("Error reading CSV file");
      }
    };

    reader.onerror = () => {
      toast.error("Error reading file");
    };

    reader.readAsText(file);
  };

  const handleConfirmUpload = async () => {
    if (!csvData) return;

    setIsProcessing(true);
    setShowUploadModal(false);

    try {
      const rows = csvData.split("\n");
      const templateHeaders = rows[0].split(",").map((header) => header.trim());

      // Map template headers to database field names
      const headerMapping = {
        "Owner 1": "owner1",
        "Owner 2": "owner2",
        Nationality: "nationality",
        Address: "address",
        Email: "email",
        "Phone Number": "phoneNumber",
        Tower: "phase",
        Floor: "block",
        "Unit Number": "lot",
        "Unit Type": "unitType",
        "Unit Size (sqm)": "lotSize",
        "Number of Occupants": "numberOfOccupants",
      };

      let successCount = 0;
      let errorCount = 0;

      // Skip header row and process data
      for (let i = 1; i < rows.length; i++) {
        if (!rows[i].trim()) continue; // Skip empty rows

        const values = rows[i].split(",").map((value) => value.trim());
        // Skip rows where all values are empty
        if (values.every((value) => value === "")) continue;

        const residentData = {};

        templateHeaders.forEach((header, index) => {
          const dbField = headerMapping[header];
          if (dbField) {
            residentData[dbField] = values[index] || "";
          }
        });

        // Add required fields
        residentData.status = "vacant";
        residentData.identifier = `Unit ${residentData.lot} - Tower ${residentData.phase}`;
        residentData.createdAt = new Date().toISOString();
        residentData.createdBy = currentUser.email;
        residentData.createdByName = currentUser.displayName;
        residentData.occupantsName = [];
        residentData.vehicles = [];
        residentData.previousOwners = [];
        residentData.notMigs = false;
        residentData.comments = [];

        // Convert all values to string
        Object.keys(residentData).forEach((key) => {
          residentData[key] = String(residentData[key]);
        });

        try {
          await createNewDocument("residents", residentData);
          successCount++;
        } catch (error) {
          console.error(`Error creating document for row ${i}:`, error);
          errorCount++;
        }
      }

      if (errorCount > 0) {
        toast.error(`Uploaded ${successCount} records, ${errorCount} failed`);
      } else {
        toast.success(`Successfully uploaded ${successCount} records`);
      }
    } catch (error) {
      console.error("Error processing CSV:", error);
      toast.error("Error processing CSV file");
    } finally {
      setIsProcessing(false);
      setCsvData(null);
      setRecordCount(0);
      setCsvPreview([]);
      setCsvHeaders([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const links = ["Owners", "Long Term Leases"];

  return (
    <div className="text-xs max-w-7xl mx-auto">
      <div className="text-gray-900 text-left font-medium mt-4 text-lg">
        Resident Management
      </div>

      {/* All links */}
      <div className="flex items-center justify-between gap-2 p-2 flex-wrap">
        <div className="links-container">
          {links.map((link, index) => (
            <div
              key={index}
              className={`${
                activeLink === link ? "activeLinkStyle" : "inactiveLinkStyle"
              } cursor-pointer`}
              onClick={() => setActiveLink(link)}
            >
              {link}
            </div>
          ))}
        </div>

        {/* buttons  */}
        <div className="flex gap-2">
          <button
            className="inline-flex items-center gap-2 px-4 py-2 bg-eleghoBlue text-white text-sm font-medium rounded-lg hover:bg-eleghoBlue/90 transition-colors duration-200 shadow-sm hover:shadow-md"
            onClick={() => setAddMode(true)}
          >
            <IoMdAdd className="text-lg" />
            <div>Add</div>
          </button>
          {/* {activeLink === "Owners" && (
            <>
              <button
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors duration-200 shadow-sm hover:shadow-md"
                onClick={downloadCSVTemplate}
              >
                <FaFileDownload className="text-lg" />
                <div>Download Template</div>
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".csv"
                className="hidden"
              />
              <button
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-sm hover:shadow-md"
                onClick={() => fileInputRef.current.click()}
                disabled={isProcessing}
              >
                <FaFileUpload className="text-lg" />
                <div>{isProcessing ? "Processing..." : "Upload CSV"}</div>
              </button>
            </>
          )} */}
        </div>
      </div>

      {/* filtering and search */}
      <div className="flex items-center justify-between p-2 border-2 border-gray-300 rounded-lg mt-2">
        <input
          className="border-2 px-4 py-2 rounded-lg border-gray-100 w-96"
          type="search"
          placeholder="Search by name, lot owner, or address"
          onChange={handleSearch}
        />
      </div>

      {/* rendering which active link is active */}
      {activeLink === "Owners" && <AllResidents searchTerm={searchTerm} />}

      {activeLink === "Long Term Leases" && (
        <LongTermLeases searchTerm={searchTerm} />
      )}

      {activeLink === "Stacking Plan" && <StackingPlan />}

      {/* //add tenant modal  */}
      <AnimatedModal
        isOpen={addMode && activeLink === "Long Term Leases"}
        onClose={() => setAddMode(false)}
      >
        <AddTenantForm setAddMode={setAddMode} addMode={addMode} />
      </AnimatedModal>

      <AnimatedModal
        isOpen={addMode && activeLink === "Owners"}
        onClose={() => setAddMode(false)}
      >
        <AddResidentForm setAddMode={setAddMode} addMode={addMode} />
      </AnimatedModal>

      {/* CSV Upload Confirmation Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Confirm Upload
              </h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setCsvData(null);
                  setRecordCount(0);
                  setCsvPreview([]);
                  setCsvHeaders([]);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-gray-600">
                  <FiFileText className="w-5 h-5" />
                  <span className="font-medium">
                    {recordCount} records found
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Please review the following before proceeding:
                </p>
                <ul className="text-sm text-gray-600 mt-2 space-y-1">
                  <li>• Empty rows will be skipped</li>
                  <li>• All data will be converted to string format</li>
                  <li>• Status will be set to "vacant" by default</li>
                  <li>• This action cannot be undone</li>
                </ul>
              </div>

              {/* Data Preview */}
              {csvPreview.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Data Preview (All {csvPreview.length} rows)
                  </h3>
                  <div className="overflow-x-auto max-h-96">
                    <table className="w-full text-xs border-collapse">
                      <thead className="sticky top-0">
                        <tr className="bg-gray-100">
                          {csvHeaders.map((header, index) => (
                            <th
                              key={index}
                              className="p-2 border text-left font-medium text-gray-700"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.map((row, rowIndex) => (
                          <tr
                            key={rowIndex}
                            className={
                              rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            {csvHeaders.map((header, cellIndex) => (
                              <td
                                key={cellIndex}
                                className="p-2 border text-gray-600"
                              >
                                {row[header] || ""}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setCsvData(null);
                    setRecordCount(0);
                    setCsvPreview([]);
                    setCsvHeaders([]);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmUpload}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Confirm Upload
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TenantManagement;
